import React, { useState, useRef, useEffect, useCallback } from 'react';
import { trainingData as trainingApi } from '../../../data/data_training';
import './TrainingPlayer.css';

/**
 * TrainingPlayer Component
 * 
 * Plays M4A audio files from training steps and streams audio chunks
 * to the WebSocket in WebM-Opus format (same as Chrome extension).
 * 
 * Only shows for active recorder sessions with training_id set.
 * Hidden for completed meetings and viewer mode.
 */
const TrainingPlayer = ({ 
  trainingId, 
  isConnected, 
  sendAudioData, 
  meetingStopped 
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoadError, setAudioLoadError] = useState(null);
  const [trainingSet, setTrainingSet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [volume, setVolume] = useState(1.0);
  const [showVolumeControl, setShowVolumeControl] = useState(false);
  
  const audioRef = useRef(null);
  const intervalRef = useRef(null);

  // Define loadStepAudio function first since it's used in useEffect and navigation functions
  const loadStepAudio = useCallback(async (stepIndex) => {
    // Early return if trainingSet is not yet loaded
    if (!trainingSet?.steps?.[stepIndex]) {
      setAudioLoadError('Step not found');
      return;
    }

    const step = trainingSet.steps[stepIndex];
    
    try {
      const mediaUrl = step.media_url;
      
      // Test if audio file exists
      const response = await fetch(mediaUrl, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error(`Audio file not found: ${mediaUrl}`);
      }
      
      setAudioUrl(mediaUrl);
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      setAudioLoadError(null);
      
    } catch (error) {
      setAudioLoadError(`Failed to load: ${step.name}`);
    }
  }, [trainingSet]);

  // Load training data from API
  useEffect(() => {
    const loadTrainingData = async () => {
      if (!trainingId) {
        setLoadError('No training ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setLoadError(null);
        
        const response = await trainingApi.getTrainingSet(trainingId);
        
        if (response.success && response.data) {
          setTrainingSet(response.data);
        } else {
          throw new Error('Failed to load training data');
        }
      } catch (error) {
        setLoadError(`Failed to load training data: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadTrainingData();
  }, [trainingId]);

  // Initialize first step when trainingSet is loaded
  useEffect(() => {
    if (trainingSet?.steps && trainingSet.steps.length > 0) {
      loadStepAudio(0);
    }
  }, [trainingSet, loadStepAudio]);

  // Get current step and total steps from loaded data
  const currentStep = trainingSet?.steps?.[currentStepIndex];
  const totalSteps = trainingSet?.steps?.length || 0;

  // Navigation functions
  const goToNextStep = useCallback(() => {
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
      loadStepAudio(currentStepIndex + 1);
    }
  }, [currentStepIndex, totalSteps, loadStepAudio]);

  const goToPreviousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      loadStepAudio(currentStepIndex - 1);
    }
  }, [currentStepIndex, loadStepAudio]);

  // Audio streaming logic (define before audio control functions)
  const startAudioStreaming = useCallback(() => {
    if (!isConnected || !audioRef.current || meetingStopped) {
      return;
    }
    
    intervalRef.current = setInterval(async () => {
      if (audioRef.current.paused || audioRef.current.ended || meetingStopped) {
        stopAudioStreaming();
        return;
      }
      
      try {
        // Create audio context for processing
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(audioRef.current.captureStream());
        const destination = audioContext.createMediaStreamDestination();
        source.connect(destination);
        
        const mediaRecorder = new MediaRecorder(destination.stream, {
          mimeType: 'audio/webm;codecs=opus'
        });
        
        const chunks = [];
        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };
        
        mediaRecorder.onstop = async () => {
          const blob = new Blob(chunks, { type: 'audio/webm;codecs=opus' });
          const arrayBuffer = await blob.arrayBuffer();
          sendAudioData(arrayBuffer);
        };
        
        mediaRecorder.start(1000);
        
        // Stop after 1 second
        setTimeout(() => {
          mediaRecorder.stop();
          audioContext.close();
        }, 1000);
        
      } catch (error) {
        // Silently handle audio streaming errors
      }
    }, 1000);
  }, [isConnected, meetingStopped, sendAudioData]);

  const stopAudioStreaming = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Audio control functions
  const handlePlay = useCallback(() => {
    if (!audioRef.current || !isConnected || meetingStopped) return;
    
    audioRef.current.play();
    setIsPlaying(true);
    setIsPaused(false);
    startAudioStreaming();
  }, [isConnected, meetingStopped, startAudioStreaming]);

  const handlePause = useCallback(() => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    setIsPlaying(false);
    setIsPaused(true);
    stopAudioStreaming();
  }, [stopAudioStreaming]);

  const handleStop = useCallback(() => {
    if (!audioRef.current) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentTime(0);
    stopAudioStreaming();
  }, [stopAudioStreaming]);

  // Volume control functions
  const handleVolumeChange = useCallback((newVolume) => {
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
      setVolume(newVolume);
    }
  }, []);

  const toggleVolumeControl = useCallback(() => {
    setShowVolumeControl(prev => {
      const newState = !prev;
      return newState;
    });
  }, [showVolumeControl]);

  const hideVolumeControl = useCallback(() => {
    setShowVolumeControl(false);
  }, []);

  // Timeline seek function
  const handleTimelineSeek = useCallback((event) => {
    if (!audioRef.current) {
      return;
    }
    
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;
    const seekTime = (clickX / width) * duration;
    
    audioRef.current.currentTime = Math.max(0, Math.min(seekTime, duration));
    setCurrentTime(audioRef.current.currentTime);
  }, [duration]);

  // Format time for display
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Safety check for connection and meeting state
  useEffect(() => {
    if (!isConnected || meetingStopped) {
      // Stop any ongoing audio streaming
      stopAudioStreaming();
      setIsPlaying(false);
      setIsPaused(false);
    }
  }, [isConnected, meetingStopped, stopAudioStreaming]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudioStreaming();
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [stopAudioStreaming, audioUrl]);

  // Handle clicking outside volume control
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showVolumeControl && !event.target.closest('.volume-control-container')) {
        hideVolumeControl();
      }
    };

    if (showVolumeControl) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVolumeControl, hideVolumeControl]);

  return (
    <div className="training-player-container">
      {isLoading && (
        <div className="training-loading">
          Loading training data...
        </div>
      )}
      
      {loadError && (
        <div className="training-error">
          Error: {loadError}
        </div>
      )}
      
      {!isLoading && !loadError && trainingSet && (
        <>
          <div className="training-title">
            {trainingSet.name || 'Liminal Meeting Training'}
          </div>
          
          <div className="training-controls-row">
            
            {/* Left Side: Audio Controls */}
            <div className="training-controls">
              <div className="controls-label">Audio Controls</div>
              
              {/* Horizontal container for audio controls and timeline */}
              <div className="audio-controls-row">
                {/* Play/Pause/Stop Buttons */}
                <div className="audio-buttons">
                  <button
                    onClick={handlePlay}
                    disabled={!audioUrl}
                    className="play-button"
                    title="Play"
                  >
                    ▶
                  </button>
                  
                  <button
                    onClick={handlePause}
                    disabled={!audioUrl}
                    className="pause-button"
                    title="Pause"
                  >
                    ⏸
                  </button>
                  
                  <button
                    onClick={handleStop}
                    disabled={!audioUrl}
                    className="stop-button"
                    title="Stop"
                  >
                    ⏹
                  </button>
                  
                  {/* Volume Icon Button with Volume Control */}
                  <div className="volume-button-container" style={{ position: 'relative' }}>
                    <button
                      onClick={toggleVolumeControl}
                      className="volume-icon-button"
                      title={`Volume: ${Math.round(volume * 100)}%`}
                    >
                      {volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
                    </button>
                    
                    {/* Collapsible Volume Control */}
                    <div 
                      className="volume-control-container"
                      style={{
                        position: 'absolute',
                        bottom: 'calc(100% + 5px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 9999,
                        display: showVolumeControl ? 'block' : 'none'
                      }}
                    >
                      <div className="volume-control-vertical">
                        <div className="volume-label">Volume</div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={volume}
                          onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                          className="volume-slider-vertical"
                          title={`Volume: ${Math.round(volume * 100)}%`}
                          orient="vertical"
                          onFocus={() => setShowVolumeControl(true)}
                        />
                        <span className="volume-value">{Math.round(volume * 100)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Timeline Control - Now positioned to the right */}
                <div className="timeline-control-horizontal">
                  <div className="timeline-label">Timeline</div>
                  <div className="timeline-bar" onClick={handleTimelineSeek}>
                    <div 
                      className="timeline-progress" 
                      style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                    />
                    <div 
                      className="timeline-handle" 
                      style={{ 
                        left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`,
                        transform: 'translateX(-50%)' /* Center the handle on the position */
                      }}
                    />
                  </div>
                  <div className="timeline-time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Middle: Step Description */}
            <div className="training-step-info">
              <div className="step-title">
                {currentStep?.name || 'No step available'}
              </div>
              <div className="step-description">
                {currentStep?.description || 'No description available'}
              </div>
              {audioLoadError && (
                <div className="audio-error">
                  {audioLoadError}
                </div>
              )}
            </div>
            
            {/* Right Side: Navigation */}
            <div className="training-navigation">
              <div className="navigation-label">Navigation</div>
              <div className="step-counter">
                Step {currentStepIndex + 1} of {totalSteps}
              </div>
              <div className="nav-buttons">
                <button
                  onClick={goToPreviousStep}
                  disabled={currentStepIndex === 0}
                  className="nav-button prev-button"
                  title="Previous Step"
                >
                  ◀
                </button>
                
                <button
                  onClick={goToNextStep}
                  disabled={currentStepIndex === totalSteps - 1}
                  className="nav-button next-button"
                  title="Next Step"
                >
                  ▶
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      
      <audio
        ref={audioRef}
        src={audioUrl}
        onLoadedMetadata={() => {
          setDuration(audioRef.current.duration);
        }}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
          }
        }}
        onEnded={() => {
          setIsPlaying(false);
          setIsPaused(false);
          stopAudioStreaming();
        }}
        volume={volume}
      />
    </div>
  );
};

export default TrainingPlayer;
